/**
 * Shared utilities for document preview functionality
 * Used by both hover preview and references sidebar systems
 */

import { DOCUMENT_SLUG_PATTERN_SRC } from '@/lib/utils/documentMetadata';

/**
 * Configuration for preview sizing and styling
 */
export const PREVIEW_CONFIG = {
  hover: {
    width: 400,
    height: 300,
    highlightClass: 'hoverOutline',
    highlightMode: 'hover' as const,
  },
  sidebar: {
    width: 450,
    height: 180,
    highlightClass: 'activeHighlight',
    highlightMode: 'active' as const,
  },
} as const;

/**
 * Parsed URL structure for document previews
 */
export interface ParsedDocumentUrl {
  /** IRI path for legislation documents (e.g., "/SK/ZZ/2011/341/20111020") */
  iri: string | null;
  /** Section ID from hash (e.g., "paragraf-76.odsek-4") */
  sectionId: string | null;
  /** Numeric document ID for decision documents */
  documentId: number | null;
}

/**
 * Parse URL to extract IRI, section ID, and document ID
 * Supports both legislation documents (IRI-based) and decision documents (ID-based)
 *
 * @param href - Full URL or relative path to parse
 * @returns Parsed components of the URL
 *
 * @example
 * // Legislation document with section (full URL)
 * parseDocumentUrl("https://lexomat.sk/sk/dokument/SK/ZZ/2011/341/20111020#paragraf-76")
 * // => { iri: "/SK/ZZ/2011/341/20111020", sectionId: "paragraf-76", documentId: null }
 *
 * @example
 * // Legislation document with section (relative path with locale)
 * parseDocumentUrl("/sk/dokument/SK/ZZ/1963/99#paragraf-278.odsek-1")
 * // => { iri: "/SK/ZZ/1963/99", sectionId: "paragraf-278.odsek-1", documentId: null }
 *
 * @example
 * // Decision document
 * parseDocumentUrl("https://lexomat.sk/sk/document/12345")
 * // => { iri: null, sectionId: null, documentId: 12345 }
 *
 * @example
 * // Direct IRI path (without locale/dokument prefix)
 * parseDocumentUrl("/SK/ZZ/2011/341/20111020#section")
 * // => { iri: "/SK/ZZ/2011/341/20111020", sectionId: "section", documentId: null }
 */
export function parseDocumentUrl(href: string): ParsedDocumentUrl {
  // Helper to extract section ID from hash
  const extractSectionId = (hash: string | undefined): string | null => {
    if (!hash) return null;
    return hash.startsWith('#') ? hash.substring(1) : hash;
  };

  // Helper to parse path and extract IRI or document ID
  const parsePath = (
    pathname: string,
    hash?: string
  ): ParsedDocumentUrl | null => {
    const sectionId = extractSectionId(hash);

    // Pattern: /{locale}/dokument/SK/... or /document/SK/... or /dokument/SK/...
    // Extracts: /SK/ZZ/2011/341/20111020
    const iriMatch = pathname.match(
      new RegExp(`/${DOCUMENT_SLUG_PATTERN_SRC}(\\/SK\\/[^#]*)`)
    );
    if (iriMatch?.[1]) {
      return { iri: iriMatch[1], sectionId, documentId: null };
    }

    // Pattern: /{locale}/dokument/12345 or /document/12345
    // Extracts: numeric document ID
    const numericMatch = pathname.match(
      new RegExp(`/${DOCUMENT_SLUG_PATTERN_SRC}\\/(\\d+)`)
    );
    if (numericMatch?.[1]) {
      return {
        iri: null,
        sectionId,
        documentId: Number.parseInt(numericMatch[1], 10),
      };
    }

    return null;
  };

  try {
    // Try parsing as full URL first
    const url = new URL(href);
    const result = parsePath(url.pathname, url.hash);
    if (result) return result;

    return {
      iri: null,
      sectionId: extractSectionId(url.hash),
      documentId: null,
    };
  } catch {
    // Not a full URL - try parsing as relative path

    // Split path and hash
    const hashIndex = href.indexOf('#');
    const pathname = hashIndex >= 0 ? href.substring(0, hashIndex) : href;
    const hash = hashIndex >= 0 ? href.substring(hashIndex) : undefined;

    // Try to parse with standard patterns
    const result = parsePath(pathname, hash);
    if (result) return result;

    // Fallback: Direct IRI path (e.g., /SK/ZZ/2011/341/20111020#section)
    const directMatch = href.match(/^(\/SK\/[^#]+)(#(.+))?$/);
    if (directMatch?.[1]) {
      return {
        iri: directMatch[1],
        sectionId: directMatch[3] ?? null,
        documentId: null,
      };
    }

    return { iri: null, sectionId: null, documentId: null };
  }
}

/**
 * Determine document type from parsed URL
 */
export function getDocumentType(
  parsed: ParsedDocumentUrl
): 'legislation' | 'decision' | 'unknown' {
  if (parsed.iri) return 'legislation';
  if (parsed.documentId) return 'decision';
  return 'unknown';
}

/**
 * Helper function to decompress HTML content if needed
 * Handles gzipped responses from document services
 *
 * @param response - Fetch response that may contain gzipped content
 * @returns Decompressed HTML string
 */
export async function decompressHtml(response: Response): Promise<string> {
  const supportsDS =
    typeof window !== 'undefined' && 'DecompressionStream' in window;

  if (supportsDS) {
    const arrayBuffer = await response.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    const isGzipped =
      bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;

    if (isGzipped) {
      const ds = new DecompressionStream('gzip');
      const blob = new Blob([arrayBuffer]);
      const decompressedStream = blob.stream().pipeThrough(ds);
      const decompressedResponse = new Response(decompressedStream);
      return await decompressedResponse.text();
    } else {
      const decoder = new TextDecoder('utf-8');
      return decoder.decode(arrayBuffer);
    }
  } else {
    return await response.text();
  }
}

/**
 * Build full preview href for TextPreview component
 * Ensures the href is absolute for proper URL parsing
 *
 * @param processedUrl - Relative URL from citation (e.g., "/sk/document/12345")
 * @param domain - Base domain (e.g., "https://lexomat.sk")
 * @returns Full absolute URL for preview
 */
export function buildPreviewHref(processedUrl: string, domain: string): string {
  const cleanDomain = domain.endsWith('/') ? domain.slice(0, -1) : domain;
  return `${cleanDomain}${processedUrl}`;
}

/**
 * External URL metadata extracted from URL string
 * Used for simple external link previews without fetching
 */
export interface ExternalUrlInfo {
  /** Full URL */
  url: string;
  /** Domain name (e.g., "example.com") */
  domain: string;
  /** Path without query string */
  path: string;
  /** Favicon URL (Google's favicon service) */
  faviconUrl: string;
  /** Display title derived from URL */
  displayTitle: string;
  /** Protocol (http or https) */
  protocol: string;
}

/**
 * Check if a URL is an external URL (not our internal document)
 * @param href - URL to check
 * @returns true if external URL
 */
export function isExternalUrl(href: string): boolean {
  // Must be a full URL with http/https
  if (!href.match(/^https?:\/\//)) {
    return false;
  }

  // Parse and check if it's an internal document
  const parsed = parseDocumentUrl(href);
  const docType = getDocumentType(parsed);

  // If we can identify it as a document, it's not external
  return docType === 'unknown';
}

/**
 * Extract metadata from an external URL without fetching
 * @param href - Full URL to extract info from
 * @returns Extracted URL information or null if invalid
 */
export function extractExternalUrlInfo(href: string): ExternalUrlInfo | null {
  try {
    const url = new URL(href);

    // Extract domain without www prefix for display
    const domain = url.hostname.replace(/^www\./, '');

    // Create a display title from the path
    // e.g., "/blog/my-article" -> "Blog - My Article"
    let displayTitle = domain;
    if (url.pathname && url.pathname !== '/') {
      const pathParts = url.pathname.split('/').filter(Boolean).slice(0, 2); // Take first 2 path segments

      if (pathParts.length > 0) {
        const formattedParts = pathParts.map((part) =>
          part
            .replace(/[-_]/g, ' ')
            .replace(/\.[^.]+$/, '') // Remove file extensions
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        );
        displayTitle = `${domain} › ${formattedParts.join(' › ')}`;
      }
    }

    // Truncate display title if too long
    if (displayTitle.length > 60) {
      displayTitle = `${displayTitle.substring(0, 57)}…`;
    }

    return {
      url: href,
      domain,
      path: url.pathname,
      faviconUrl: `https://www.google.com/s2/favicons?domain=${domain}&sz=32`,
      displayTitle,
      protocol: url.protocol.replace(':', ''),
    };
  } catch {
    return null;
  }
}
